import { Box, Typography, Sheet } from "@mui/joy";
import { Factory, Heart, Database, Building2, Sun, Fuel } from "lucide-react";
import { INDUSTRIES } from "../data/products";
const colors = {
  brandRed: "var(--brand-red)",
  brandDark: "var(--brand-dark)",
  brandBlue: "var(--brand-blue)",
  slate50: "#f8fafc",
  slate100: "#f1f5f9",
  slate200: "#e2e8f0",
  slate400: "#94a3b8",
  slate500: "#64748b",
  red50: "#fef2f2",
  blue700: "#1d4ed8",
  white: "#ffffff",
};

const fonts = {
  display: "var(--font-display)",
  sans: "var(--font-sans)",
  mono: "var(--font-mono)",
};
const themeColors = {
  obsidian: "#0b0f19",
  safety: "#f97316", // fallback orange/safety color
  ink: "#111827",
  concrete: "#f3f4f6",
  mutedForeground: "#6b7280",
};
export default function Industries() {
  // Map icons to the corresponding industry name keywords
  const getIcon = (name) => {
    if (name.includes("Manufacturing")) return Factory;
    if (name.includes("Healthcare")) return Heart;
    if (name.includes("Data")) return Database;
    if (name.includes("Infrastructure")) return Building2;
    if (name.includes("Solar")) return Sun;
    if (name.includes("Oil")) return Fuel;
    return Factory;
  };

  return (
    <Box
      id="industries"
      component="section"
      sx={{
        py: "96px",
        backgroundColor: colors.white,
        scrollMarginTop: "48px",
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: "16px", sm: "24px", lg: "32px" },
        }}
      >
        {/* Section Title */}
        <Box
          sx={{
            textAlign: "center",
            maxWidth: "768px",
            mx: "auto",
            mb: "64px",
          }}
        >
          <Typography
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            sx={{
              fontFamily: "monospace",
              color: themeColors.safety,
              mb: 4,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            [ Target Industry Verticals ]
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: "Cabinet Grotesk, system-ui, sans-serif;",
              fontSize: { xs: "30px", sm: "36px" },
              fontWeight: 800,
              letterSpacing: "-0.025em",
              color: colors.brandDark,
              mt: "16px",
              mb: "16px",
              lineHeight: 1.2,
            }}
          >
            Industries We Serve
          </Typography>

          <Box
            sx={{
              width: "64px",
              height: "4px",
              backgroundColor: colors.brandRed,
              mx: "auto",
              mb: "24px",
              borderRadius: "9999px",
            }}
          />

          <Typography
            sx={{
              fontFamily: fonts.sans,
              color: colors.slate500,
              lineHeight: 1.625,
              fontSize: { xs: "14px", sm: "16px" },
            }}
          >
            ITL Technical Service custom designs and fabricates stabilizers,
            transformers, and conditioners for diverse industrial environments
            where power quality is a critical mission constraint.
          </Typography>
        </Box>

        {/* Grid of Industries */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            },
            gap: "32px",
          }}
        >
          {INDUSTRIES.map((ind, idx) => {
            const Icon = getIcon(ind.name);

            return (
              <Sheet
                key={idx}
                variant="plain"
                sx={{
                  backgroundColor: colors.slate50,
                  border: `1px solid ${colors.slate100}e6`,
                  borderRadius: "16px",
                  p: "24px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "all 0.3s ease",
                  cursor: "default",

                  "&:hover": {
                    backgroundColor: colors.white,
                    boxShadow:
                      "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                    borderColor: colors.slate200,
                    transform: "translateY(-4px)",
                  },

                  /* Icon box changes on card hover */
                  "&:hover .app-icon-box": {
                    backgroundColor: colors.brandRed,
                  },

                  /* Icon becomes white on card hover */
                  "&:hover .app-icon": {
                    color: colors.white,
                  },

                  "&:hover .app-card-title": {
                    color: colors.brandBlue,
                  },

                  "&:hover .app-card-footer": {
                    color: colors.brandBlue,
                  },
                }}
              >
                <Box>
                  <Box
                    className="app-icon-box"
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      backgroundColor: colors.white,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: "24px",
                      boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Icon
                      className="app-icon"
                      size={24}
                      style={{
                        color: colors.slate500,
                        transition: "color 0.3s ease",
                      }}
                    />
                  </Box>

                  <Typography
                    component="h3"
                    className="app-card-title"
                    sx={{
                      fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                      fontWeight: 700,
                      fontSize: "18px",
                      color: colors.brandDark,
                      mb: "12px",
                      transition: "color 0.2s ease",
                    }}
                  >
                    {ind.name}
                  </Typography>

                  <Typography
                    sx={{
                      color: colors.slate500,
                      fontSize: { xs: "12px", sm: "14px" },
                      fontFamily: fonts.sans,
                      lineHeight: 1.625,
                      mb: "24px",
                    }}
                  >
                    {ind.description}
                  </Typography>
                </Box>

                <Box
                  className="app-card-footer"
                  sx={{
                    borderTop: `1px solid ${colors.slate100}`,
                    pt: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: colors.slate400,
                    fontFamily: fonts.mono,
                    transition: "color 0.2s ease",
                  }}
                >
                  <span>Custom Configurations</span>
                  <span>100% Load Tested</span>
                </Box>
              </Sheet>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
